/**
 * Search Deals Handler
 * Query handler for full-text search across deals
 */

import type { ToolExecutionContext } from '../../types';
import { QueryHandlerBase } from '../base';
import { createUserGraphQLClient } from '../../utils/graphql-client';

/**
 * Search deals handler
 * Performs full-text search across deal titles and descriptions
 */
export class SearchDealsHandler extends QueryHandlerBase<any> {
  id = 'search_deals';

  schema = {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query for deal titles and descriptions',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of results (default: 20, max: 100)',
        default: 20,
      },
    },
    required: ['query'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!params.query || params.query.trim().length === 0) {
      throw new Error('Search query is required');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for deal search');
    }

    try {
      const { GetDealssDocument } = await import('@/src/gql/graphql');
      const client = createUserGraphQLClient(context.userToken);

      const limit = Math.min(params.limit || 20, 100);
      const searchTerm = `%${params.query}%`;

      // Build filter for title or description search
      const filter = {
        or: [
          {
            title: {
              ilike: searchTerm,
            },
          },
          {
            description: {
              ilike: searchTerm,
            },
          },
        ],
      };

      const result = await client.query({
        query: GetDealssDocument,
        variables: {
          first: limit,
          filter,
          orderBy: [{ updated_at: 'DescNullsLast' }],
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const deals = data?.dealsCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

      return {
        query: params.query,
        results: deals.map((d: any) => ({
          ...d,
          viewInApp: `[${d.title}](/deals/${d.id})`,
        })),
        total: deals.length,
        hasMore: deals.length >= limit,
      };
    } catch (error) {
      throw new Error(`Deal search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
